import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChartBox, DonutChart, DonutsComparisonWrapper } from "@unified-checkout/design-system";
import { SkeletonLoader } from "@unified-checkout/design-system";
import type { ApexOptions } from "apexcharts";
import type { ILegendItem } from "libs/design-system/src/types/charts/chart_box";
import type { IHomeApisParams } from "@app/dashboard/types/innerLayout/home";
import { ComponentError } from "@app/dashboard/components/shared/ComponentError";
import { getMethodsUsage } from "@app/dashboard/services/inner_layout/home/charts";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { useFiltersStore } from "@app/dashboard/store/home/filters";
import { useLangStore } from "@app/dashboard/store/lang";
import { getDonutChartOptions } from "@app/dashboard/utils/charts";

const LegendListColors = [
  '#144DFF', '#F79009', '#04B389', '#79716B', '#875BF7',
  '#C3B5FD', '#7294FF', '#0C2E99', '#D444F1', '#026B52'
];

const IDS = { main: "__main-donut-chart", compare: "__compare-donut-chart" }

const MethodsUsage = ({ apiParams }: { apiParams: IHomeApisParams }) => {

  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const { filters } = useFiltersStore();
  const { lang } = useLangStore();

  const [chartOptions, setChartOptions] = useState<ApexOptions>();
  const [centerLabel, setCenterLabel] = useState({ main: "", compare: "" });

  const {
    isLoading,
    isError,
    data: response,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_METHODS_USAGE, filters],
    queryFn: () => getMethodsUsage(apiParams),
    enabled: Object.values(filters).every(value => value !== undefined)
  });

  const chartData = response?.data;
  const mainData = chartData?.data;
  const compareData = chartData?.compare.data;

  const mainSeries = mainData?.map((obj: any) => obj.value ?? 0) || [];
  const compareSeries = compareData?.map((obj: any) => obj.value ?? 0) || [];

  const titles = mainData?.map((obj: any) => obj.title ?? "");

  const legendList: ILegendItem[] = titles?.map((title: any, index: number) => (
    { color: LegendListColors[index], label: title }
  ));

  useEffect(() => {
    return () => queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_METHODS_USAGE] });
  }, []);

  useEffect(() => {
    setChartOptions(
      getDonutChartOptions(
        t,
        lang,
        setCenterLabel,
        IDS,
        mainSeries,
        compareSeries
      )
    );
  }, [chartData, lang]);

  return (
    isError ?
      <ChartBox>
        <ComponentError text={t('innerLayout.errors.unableToRetrieveData')} />
      </ChartBox>
      :
      isLoading ?
        <SkeletonLoader noOfRows={8} />
        :
        (
          response ?
            <ChartBox
              title={chartData[`${lang}_title`]}
              info={chartData[`info_${lang}`]}
              legendList={legendList}
              legendPlacement="bottom"
            >
              <DonutsComparisonWrapper
                series={[
                  { data: mainSeries },
                  { data: compareSeries }
                ]}
              >
                <DonutChart
                  key={IDS.main}
                  id={IDS.main}
                  options={chartOptions}
                  centerLabel={centerLabel.main || t('innerLayout.homePage.charts.current')}
                  opacity="solid"
                />
                <DonutChart
                  key={IDS.compare}
                  id={IDS.compare}
                  options={chartOptions}
                  centerLabel={centerLabel.compare || t('innerLayout.homePage.charts.previous')}
                  opacity="faded"
                />
              </DonutsComparisonWrapper>
            </ChartBox>
            :
            <SkeletonLoader noOfRows={8} />
        )
  )
}

export default MethodsUsage;